import { useState } from 'react'
import Analytics from 'utils/googleAnalytics'

import ToastService from 'services/toast.service'
import CollectionService from 'services/collection.service'

import { HUMAN_GAME, GAME_IDS } from 'types/deck'

import { useAppSelector } from 'app-redux/hooks'

import PhatButton from 'components/formElements/PhatButton'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import PhatDropdown from 'components/formElements/PhatDropdown'
import LabelOptions from 'components/collectionV2/cardDetails/Form/LabelOptions'
import Icon from 'components/elements/Icon'
import CursorCardWrapper from 'components/card/CursorCardWrapper'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'

import { CategoryType } from 'types/deck'
import { CollectionAddOptionType } from 'services/apiTypes/collection.types'

import styles from './multiAddToCollection.module.scss'

type Props = {
  open: boolean
  onClose: (flag: boolean) => void
  selectCardsViaCategories?: boolean
  cardIds?: string[] // If cardIds is not present, we assume use the entire cardMap
}

const MultiAddToCollection = ({ open, onClose, cardIds, selectCardsViaCategories }: Props) => {
  const categories = useAppSelector(state => state.deck.categories)
  const cardMap = useAppSelector(state => state.deck.cardMap)

  const getDefaultCategories = (categories: Record<string, CategoryType>) => {
    return Object.keys(categories).reduce(
      (acc, category) => ({ ...acc, [category]: categories[category]?.includedInDeck ?? true }),
      {} as Record<string, boolean>,
    )
  }

  const [includedCategories, setIncludedCategories] = useState(getDefaultCategories(categories))
  const [removedIds, setRemovedIds] = useState<Record<string, boolean>>({})
  const [addMode, setAddMode] = useState<CollectionAddOptionType>('Create')
  const [loading, setLoading] = useState(false)
  const [game, setGame] = useState(HUMAN_GAME.Paper)
  const [updatedTags, setUpdatedTags] = useState(new Array<number>())

  const selectedCards = (cardIds || Object.keys(cardMap)).map(id => cardMap[id]).filter(card => !!card) // If we remove a card from the card map while multi-selected, this will crash unless we filter out unfound cards.
  const filteredCards = selectedCards.filter(card => {
    if (selectCardsViaCategories && includedCategories[card.categories[0] || '']) return true
    if (!selectCardsViaCategories && !removedIds[card.id]) return true
    return false
  })
  const totalCards = filteredCards
    .filter(card => !selectCardsViaCategories || includedCategories[card.categories[0]])
    .reduce((acc, card) => (acc += card.qty), 0)

  const handleOnClose = () => {
    setRemovedIds({})
    setIncludedCategories(getDefaultCategories(categories))
    onClose(false)
  }

  const handleAddToCollection = () => {
    Analytics.signalEvent('Adding bulk cards to collection.')

    setLoading(true)

    const cardsToAdd = filteredCards.map(card => ({
      card: card.cardId || '',
      quantity: card.qty,
      foil: card.modifier === 'Foil',
      modifier: card.modifier,
      language: 1,
      condition: 1,
      tags: updatedTags,
      addOption: addMode,
      game: game,
    }))

    return CollectionService.create(cardsToAdd)
      .then(() => {
        ToastService.create('Cards added to your collection', 'Collection Service', 'success')
        handleOnClose()
      })
      .catch(() => ToastService.create('Unable to add cards to your collection', 'Collection', 'error'))
      .finally(() => setLoading(false))
  }

  const handleRemoveSelection = (cardId: string) => {
    setRemovedIds({ ...removedIds, [cardId]: !removedIds[cardId] })
  }

  return (
    <GlobalOverlayStack
      open={open}
      onClose={handleOnClose}
      mobileToolbarOptions={[
        {
          label: 'Add',
          onClick: handleAddToCollection,
          icon: 'circle-plus',
        },
      ]}>
      <div className={styles.container}>
        <h2>Add to your collection</h2>
        {selectCardsViaCategories && (
          <div>
            This will add all cards from each category in this deck to your personal collection, including Sideboard and
            Maybeboard. If you would like to exclude certain categories from this operation, you can unselect them
            below.
          </div>
        )}
        <div className={styles.columns}>
          <div className={styles.cards}>
            <div className={styles.header}>{totalCards} card(s) selected</div>
            <div className={styles.table}>
              {!selectCardsViaCategories &&
                selectedCards.map(card => (
                  <div key={card.id} className={styles.row}>
                    <CursorCardWrapper card={card} className={styles.fill}>
                      <button className={styles.button} onClick={() => handleRemoveSelection(card.id)}>
                        <Icon name={removedIds[card.id] ? 'square outline' : 'check square'} />
                        {card.name} ({card.setCode}) {card.collectorNumber}
                      </button>
                    </CursorCardWrapper>
                  </div>
                ))}
              {selectCardsViaCategories &&
                Object.keys(categories).map(category => (
                  <div key={category} className={styles.row}>
                    <div className={styles.fill}>
                      <button
                        className={styles.button}
                        onClick={() =>
                          setIncludedCategories({ ...includedCategories, [category]: !includedCategories[category] })
                        }>
                        <Icon name={includedCategories[category] ? 'check square' : 'square outline'} />
                        {category}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={styles.options}>
            <PhatDropdown
              header="How to handle cards already in your collection"
              value={addMode}
              className={styles.margin}
              triggerWrapperClassName={styles.fullWidth}
              options={[
                {
                  label: 'Add all cards as new collection records',
                  id: 'Create',
                  onClick: () => setAddMode('Create'),
                },
                {
                  label: 'Update existing collection records if they exist',
                  id: 'Update',
                  onClick: () => setAddMode('Update'),
                },
                {
                  label: 'Skip existing collection records if they exist',
                  id: 'Skip',
                  onClick: () => setAddMode('Skip'),
                },
              ]}
            />
            <PhatDropdown
              header="Select collection to import into"
              className={styles.margin}
              triggerWrapperClassName={styles.fullWidth}
              value={GAME_IDS[game]}
              options={Object.keys(HUMAN_GAME).map(game_type => ({
                label: game_type,
                onClick: () => setGame(HUMAN_GAME[game_type]),
                id: game_type,
              }))}
            />

            <LabelOptions onTagsChange={setUpdatedTags} updatedTags={updatedTags} />
            {!!updatedTags.length && addMode === 'Update' && (
              <div className={styles.reminderText}>
                When updating collection records, labels will be added to any existing labels for those cards.
              </div>
            )}
          </div>
        </div>
        <div className={styles.controls}>
          <PhatButton disabled={loading} icon="plus" color="green" onClick={handleAddToCollection}>
            {!loading ? (
              'Add to my collection'
            ) : (
              <>
                <SimpleSpinner size="xSmall" /> Adding cards...
              </>
            )}
          </PhatButton>
        </div>
      </div>
    </GlobalOverlayStack>
  )
}

export default MultiAddToCollection
